import * as React from 'react';
import { Theme, alpha } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import * as yup from 'yup';
import { useFormik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useToastError } from '@hooks';
import { Spot, useUpdateSpotAtmosphereMutation, UpdateSpotAtmosphereInput } from '@graphql';

interface RenderSwitchProps {
  atmosphere: keyof UpdateSpotAtmosphereInput;
}
interface SpotAtmosphereProps {
  isActive: boolean;
  account: Spot;
}

export function SpotAtmosphere(props: Readonly<SpotAtmosphereProps>) {
  const { isActive, account } = props;
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const [updateSpotAtmosphere] = useUpdateSpotAtmosphereMutation();

  const initialValues = React.useMemo(() => {
    return Object.entries(account.atmosphere)
      .filter(([key, value]) => !['__typename', 'id', 'updatedAt'].includes(key))
      .reduce(
        (acc, [key, value]) => {
          acc[key as keyof UpdateSpotAtmosphereInput] = value;
          return acc;
        },
        {} as Record<keyof UpdateSpotAtmosphereInput, boolean | null>
      );
  }, [account.atmosphere]);

  const validationSchema = React.useMemo(() => {
    return yup.object(
      Object.keys(account.atmosphere)
        .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
        .reduce(
          (acc, key) => {
            acc[key as keyof UpdateSpotAtmosphereInput] = yup.boolean().nullable().defined();
            return acc;
          },
          {} as Record<keyof UpdateSpotAtmosphereInput, yup.BooleanSchema<boolean | null>>
        )
    );
  }, [account.atmosphere]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (formik.dirty) {
        updateSpotAtmosphere({
          variables: { input: values },
          onCompleted: ({ updateSpotAtmosphere }) => {
            formik.resetForm({ values });
            toast(t('spotAtmosphere.toast.success', { ns: 'field' }), { type: 'success' });
          },
          onError: toastError,
        });
      }
    },
  });

  React.useEffect(() => {
    return () => {
      // trigger the mutation when the component is not anymore in view and the form is dirty
      if (formik.dirty) {
        formik.submitForm();
      }
    };
  }, [isActive, formik.dirty]);

  if (account?.__typename !== 'Spot') {
    return null;
  }

  return (
    <Stack width="100%" spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          p: 3,
          borderRadius: (theme: Theme) => theme.borderRadius,
          backgroundColor: alpha('#fff', 0.1),
        }}
      >
        <Stack>
          <Typography fontWeight="bold">{t('spotAtmosphere.label', { ns: 'field' })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('spotAtmosphere.caption', { ns: 'field' })}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        divider={<Divider sx={{ backgroundColor: (theme: Theme) => theme.palette.grey[800] }} />}
      >
        {Object.keys(account.atmosphere)
          .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
          .map((atmosphere) => (
            <Stack
              key={atmosphere}
              spacing={2}
              direction="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.primary">
                {t(`atmosphere.${atmosphere}`, { ns: 'spot' })}
              </Typography>
              <RenderSwitch atmosphere={atmosphere as RenderSwitchProps['atmosphere']} formik={formik} />
            </Stack>
          ))}
      </Stack>
    </Stack>
  );
}

function RenderSwitch(props: RenderSwitchProps & { formik: FormikProps<any> }) {
  const { atmosphere, formik } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    formik.setFieldValue(name, checked);
  };

  return (
    <Switch
      name={atmosphere}
      checked={!!formik.values[atmosphere]}
      onChange={handleChange}
      color={'success'}
    />
  );
}
